import React, { useCallback, useEffect, useMemo, useState } from "react";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import Dropdown from "../../common/dropDown";
import DateRangeFilter, {
  DEFAULT_PRESETS,
} from "../../common/DateRangeFilter";
import { useLoader } from "../../../context/LoaderContext";
import { fetchAuditLog } from "./esourceService";
import { exportEsourceAuditLogToExcel } from "./esourceAuditExport";
import {
  DATE_RANGE_INITIAL,
  matchesDateRange,
} from "./esourceTemplateListUtils";
import "./ESourcePages.scss";

const ACTION_LABELS = {
  TemplateCreated: "Form created",
  TemplateDraftSaved: "Draft saved",
  TemplatePublished: "Published",
  TemplateDraftVersionCreated: "Draft version created",
  InstanceCreated: "Form record created",
};

const ACTION_VISUAL = {
  TemplateCreated: { tone: "purple", icon: "fas fa-file-alt" },
  TemplateDraftSaved: { tone: "amber", icon: "fas fa-save" },
  TemplatePublished: { tone: "green", icon: "fas fa-check" },
  TemplateDraftVersionCreated: { tone: "orange", icon: "fas fa-code-branch" },
  InstanceCreated: { tone: "purple", icon: "fas fa-file-alt" },
};

function formatActionTitle(action) {
  const key = String(action || "").trim();
  if (ACTION_LABELS[key]) return ACTION_LABELS[key];
  if (!key) return "Update";
  return key
    .replace(/^Template/, "")
    .replace(/^Instance/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
}

function getActionVisual(action) {
  return (
    ACTION_VISUAL[String(action || "").trim()] || {
      tone: "blue",
      icon: "fas fa-circle",
    }
  );
}

function getVersionLabel(row) {
  if (row?.VersionLabel) return String(row.VersionLabel);
  if (row?.VersionNo != null && row.VersionNo !== "") {
    return `v${row.VersionNo}`;
  }
  return null;
}

function formatDayHeading(value) {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTimeOnly(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
}

function dayKey(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function displayChangeValue(value) {
  const text = String(value || "").trim();
  if (!text || text === "—" || text === "--") return "";
  return text;
}

function hasChangeValue(value) {
  return Boolean(displayChangeValue(value));
}

/** Build change rows for timeline diff lines. */
function getAuditChanges(row) {
  const action = String(row?.Action || "").trim();
  const oldValue = String(row?.OldValue || "").trim();
  const newValue = String(row?.NewValue || "").trim();
  const fieldId = String(row?.FieldId || "").trim();
  const versionLabel = getVersionLabel(row);
  const changes = [];

  if (fieldId === "Form fields" || fieldId === "Fields") {
    const removed = splitCsv(oldValue);
    const added = splitCsv(newValue);
    if (removed.length) {
      changes.push({
        label: "Removed",
        oldValue: removed.join(", "),
        newValue: "",
      });
    }
    if (added.length) {
      changes.push({
        label: "Added",
        oldValue: "",
        newValue: added.join(", "),
      });
    }
    return changes;
  }

  if (oldValue || (fieldId && (oldValue || newValue))) {
    changes.push({
      label: fieldId || "Value",
      oldValue: oldValue || "",
      newValue: newValue || "",
    });
    return changes;
  }

  if (action === "TemplateCreated") {
    changes.push({
      label: "Version",
      oldValue: "",
      newValue: versionLabel || "v1",
    });
  } else if (action === "TemplateDraftVersionCreated") {
    changes.push({
      label: "Draft",
      oldValue: "Published",
      newValue: versionLabel || "New draft",
    });
  } else if (action === "TemplatePublished") {
    changes.push({
      label: "Status",
      oldValue: "Draft",
      newValue: versionLabel ? `${versionLabel} published` : "Published",
    });
  }

  return changes;
}

export default function ESourceFormAuditModal({
  open,
  onClose,
  formTemplateId,
  formCode = "",
  formName = "",
}) {
  const { showLoader, hideLoader } = useLoader();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState([]);
  const [versionFilter, setVersionFilter] = useState([]);
  const [dateRange, setDateRange] = useState(DATE_RANGE_INITIAL);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!formTemplateId) return;
    setListLoading(true);
    showLoader();
    try {
      const auditRes = await fetchAuditLog({ FormTemplateId: formTemplateId });
      if (auditRes.success) setRows(auditRes.rows || []);
      else setRows([]);
    } finally {
      hideLoader();
      setListLoading(false);
    }
  }, [formTemplateId, showLoader, hideLoader]);

  useEffect(() => {
    if (!open || !formTemplateId) return;
    setSearch("");
    setActionFilter([]);
    setVersionFilter([]);
    setDateRange(DATE_RANGE_INITIAL);
    void load();
  }, [open, formTemplateId, load]);

  const actionOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const a = String(row.Action || "").trim();
      if (a) set.add(a);
    });
    return Array.from(set)
      .sort()
      .map((action) => ({
        value: action,
        label: formatActionTitle(action),
      }));
  }, [rows]);

  const versionOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const label = getVersionLabel(row);
      if (label) set.add(label);
    });
    return Array.from(set)
      .sort((a, b) => {
        const na = Number(String(a).replace(/^v/i, "")) || 0;
        const nb = Number(String(b).replace(/^v/i, "")) || 0;
        return nb - na;
      })
      .map((version) => ({ value: version, label: version }));
  }, [rows]);

  const selectedActionValues = useMemo(
    () =>
      (Array.isArray(actionFilter) ? actionFilter : [])
        .map((item) =>
          typeof item === "string" ? item : String(item?.value || "").trim(),
        )
        .filter(Boolean),
    [actionFilter],
  );

  const selectedVersionValues = useMemo(
    () =>
      (Array.isArray(versionFilter) ? versionFilter : [])
        .map((item) =>
          typeof item === "string" ? item : String(item?.value || "").trim(),
        )
        .filter(Boolean),
    [versionFilter],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const action = String(row.Action || "").trim();
      if (
        selectedActionValues.length &&
        !selectedActionValues.includes(action)
      ) {
        return false;
      }
      const versionLabel = getVersionLabel(row);
      if (
        selectedVersionValues.length &&
        !selectedVersionValues.includes(versionLabel || "")
      ) {
        return false;
      }
      if (!matchesDateRange(row.PerformedAt, dateRange)) return false;
      if (!q) return true;
      const changes = getAuditChanges(row);
      const hay = [
        row.Action,
        formatActionTitle(row.Action),
        versionLabel,
        row.PerformedByName,
        row.NewValue,
        row.OldValue,
        row.Reason,
        row.ErrorCode,
        row.FieldId,
        ...changes.flatMap((c) => [c.label, c.oldValue, c.newValue]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    rows,
    search,
    selectedActionValues,
    selectedVersionValues,
    dateRange,
  ]);

  const groupedRows = useMemo(() => {
    const groups = [];
    const indexByKey = new Map();
    filteredRows.forEach((row) => {
      const key = dayKey(row.PerformedAt);
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({
          key,
          label: formatDayHeading(row.PerformedAt),
          rows: [],
        });
      }
      groups[indexByKey.get(key)].rows.push(row);
    });
    return groups;
  }, [filteredRows]);

  const clearFilters = () => {
    setSearch("");
    setActionFilter([]);
    setVersionFilter([]);
    setDateRange(DATE_RANGE_INITIAL);
  };

  const titleCode = formCode || rows[0]?.FormCode || "";
  const titleName = formName || rows[0]?.FormName || "Form";
  const subtitle = [titleCode, titleName].filter(Boolean).join(" · ");

  const handleExportExcel = useCallback(async () => {
    if (!filteredRows.length || exporting) return;
    setExporting(true);
    try {
      const exportRows = [];
      filteredRows.forEach((row) => {
        const action = formatActionTitle(row.Action);
        const version = getVersionLabel(row) || "";
        const performedBy =
          String(row.PerformedByName || "").trim() || "";
        const reason = String(row.Reason || "").trim();
        const changes = getAuditChanges(row);
        if (!changes.length) {
          exportRows.push({
            performedAt: row.PerformedAt,
            action,
            version,
            field: String(row.FieldId || "").trim(),
            oldValue: displayChangeValue(row.OldValue),
            newValue: displayChangeValue(row.NewValue),
            performedBy,
            reason,
          });
          return;
        }
        changes.forEach((change) => {
          exportRows.push({
            performedAt: row.PerformedAt,
            action,
            version,
            field: change.label || "",
            oldValue: displayChangeValue(change.oldValue),
            newValue: displayChangeValue(change.newValue),
            performedBy,
            reason,
          });
        });
      });
      await exportEsourceAuditLogToExcel(exportRows, {
        formLabel: titleCode || titleName || "form",
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, filteredRows, titleCode, titleName]);

  return (
    <CtmsWorkspaceModal
      open={open}
      onClose={onClose}
      title="Audit log"
      titleId="esource-form-audit-modal-title"
      titleExtras={
        subtitle ? (
          <span className="esource-audit-modal__subtitle" title={subtitle}>
            {subtitle}
          </span>
        ) : null
      }
      headerActions={
        <div className="esource-audit-modal__actions">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary esource-audit-modal__export"
            onClick={() => void handleExportExcel()}
            disabled={listLoading || exporting || filteredRows.length === 0}
          >
            <i className="fas fa-file-excel me-1" aria-hidden="true" />
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary esource-audit-modal__refresh"
            onClick={() => void load()}
            disabled={listLoading}
          >
            <i className="fas fa-sync-alt me-1" aria-hidden="true" />
            Refresh
          </button>
        </div>
      }
      dialogClassName="ctms-workspace-modal__dialog--full"
      contentClassName="esource-audit-modal"
      className="esource-audit-modal-host"
    >
      {listLoading ? (
        <p className="esource-audit-modal__status">Loading audit log…</p>
      ) : rows.length === 0 ? (
        <div className="esource-audit-modal__empty">
          <p className="mb-1">No audit entries yet for this form.</p>
          <p className="text-muted mb-0">
            Create, save draft, publish, and draft-version actions will appear
            here with the form version and field changes.
          </p>
        </div>
      ) : (
        <div className="esource-audit-log">
          <div className="esource-audit-log__main">
            <p className="esource-audit-log__summary">
              Showing {filteredRows.length} of {rows.length} entr
              {rows.length === 1 ? "y" : "ies"}
            </p>
            {filteredRows.length === 0 ? (
              <div className="esource-audit-log__empty-filter">
                <p className="mb-2">No entries match the current filters.</p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="esource-audit-log__timeline">
                {groupedRows.map((group, groupIndex) => (
                  <section
                    key={group.key}
                    className="esource-audit-log__day"
                    aria-label={group.label}
                  >
                    <h3 className="esource-audit-log__day-label">
                      {group.label}
                    </h3>
                    <ol className="esource-audit-log__list">
                      {group.rows.map((row, index) => {
                        const title = formatActionTitle(row.Action);
                        const when = formatTimeOnly(row.PerformedAt);
                        const versionLabel = getVersionLabel(row);
                        const by =
                          String(row.PerformedByName || "").trim() || "—";
                        const reason = String(row.Reason || "").trim();
                        const changes = getAuditChanges(row);
                        const visual = getActionVisual(row.Action);
                        const isLast =
                          groupIndex === groupedRows.length - 1 &&
                          index === group.rows.length - 1;
                        return (
                          <li
                            key={row.AuditId || `${row.PerformedAt}-${index}`}
                            className="esource-audit-log__item"
                          >
                            <div
                              className="esource-audit-log__rail"
                              aria-hidden="true"
                            >
                              <span
                                className={`esource-audit-log__icon esource-audit-log__icon--${visual.tone}`}
                              >
                                <i className={visual.icon} />
                              </span>
                              {!isLast ? (
                                <span className="esource-audit-log__line" />
                              ) : null}
                            </div>
                            <article className="esource-audit-log__card">
                              <header className="esource-audit-log__head">
                                <div className="esource-audit-log__title-wrap">
                                  <h4 className="esource-audit-log__title">
                                    {title}
                                  </h4>
                                  {changes.length ? (
                                    <span className="esource-audit-log__change-count">
                                      · {changes.length} change
                                      {changes.length === 1 ? "" : "s"}
                                    </span>
                                  ) : null}
                                  {versionLabel ? (
                                    <span className="esource-audit-log__version">
                                      {versionLabel}
                                    </span>
                                  ) : null}
                                </div>
                                {when ? (
                                  <time
                                    className="esource-audit-log__when"
                                    dateTime={row.PerformedAt || undefined}
                                  >
                                    {when}
                                  </time>
                                ) : null}
                              </header>

                              {reason ? (
                                <p className="esource-audit-log__reason">
                                  <span>Reason</span> {reason}
                                </p>
                              ) : null}

                              {changes.length ? (
                                <ul className="esource-audit-log__changes">
                                  {changes.map((change, changeIndex) => {
                                    const oldText = displayChangeValue(
                                      change.oldValue,
                                    );
                                    const newText = displayChangeValue(
                                      change.newValue,
                                    );
                                    const showBoth =
                                      hasChangeValue(change.oldValue) &&
                                      hasChangeValue(change.newValue);
                                    return (
                                      <li
                                        key={`${
                                          row.AuditId || index
                                        }-c-${changeIndex}`}
                                        className="esource-audit-log__change"
                                      >
                                        {change.label ? (
                                          <span className="esource-audit-log__change-label">
                                            {change.label}
                                          </span>
                                        ) : null}
                                        {showBoth ? (
                                          <>
                                            <span className="esource-audit-log__value esource-audit-log__value--old">
                                              {oldText}
                                            </span>
                                            <span
                                              className="esource-audit-log__arrow"
                                              aria-hidden="true"
                                            >
                                              <i className="fas fa-arrow-right" />
                                            </span>
                                            <span className="esource-audit-log__value esource-audit-log__value--new">
                                              {newText}
                                            </span>
                                          </>
                                        ) : oldText ? (
                                          <span className="esource-audit-log__value esource-audit-log__value--old">
                                            {oldText}
                                          </span>
                                        ) : (
                                          <span className="esource-audit-log__value esource-audit-log__value--new">
                                            {newText}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : null}

                              <div className="esource-audit-log__actor">
                                {by}
                              </div>
                            </article>
                          </li>
                        );
                      })}
                    </ol>
                  </section>
                ))}
              </div>
            )}
          </div>

          <aside
            className="esource-audit-filters"
            aria-label="Audit log filters"
          >
            <div className="esource-audit-filters__head">
              <h3 className="esource-audit-filters__title">Filters</h3>
              <button
                type="button"
                className="btn btn-sm btn-link px-0"
                onClick={clearFilters}
              >
                Reset
              </button>
            </div>
            <label className="esource-audit-filters__field">
              <span>Search</span>
              <input
                type="search"
                className="form-control form-control-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search action, user, field…"
              />
            </label>
            <div className="esource-audit-filters__field">
              <span>Action</span>
              <Dropdown
                options={actionOptions}
                isMulti
                selected={actionFilter}
                onSelect={setActionFilter}
                showSearch
                showMultiSelectedTooltip
              />
            </div>
            <div className="esource-audit-filters__field">
              <span>Version</span>
              <Dropdown
                options={versionOptions}
                isMulti
                selected={versionFilter}
                onSelect={setVersionFilter}
                showSearch
                showMultiSelectedTooltip
              />
            </div>
            <div className="esource-audit-filters__field">
              <span>Date range</span>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                presetOptions={DEFAULT_PRESETS}
                placeholder="From date – To date"
              />
            </div>
          </aside>
        </div>
      )}
    </CtmsWorkspaceModal>
  );
}
